// DISCLAIMER
// Copyright 2026 ArangoDB GmbH, Cologne, Germany

import React from "react";
import yaml from "js-yaml";
import { Label, Message, SemanticCOLORS } from "semantic-ui-react";
import { ChartDiagnostic, ChartDiagnosticSeverity, ChartValueSource } from "../../api/charts/v1/icharts";

const toBase64 = (text: string): string => {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
};

const fromBase64 = (encoded: string): string => {
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
};

// Chart values travel as a base64 encoded JSON object (a bytes field), the editors show them as YAML.
// decodeValues returns them as YAML, or an empty string when there are no values.
export const decodeValues = (values?: string): string => {
  if (!values || !values.trim()) return "";
  try {
    const parsed = JSON.parse(fromBase64(values));
    return parsed && Object.keys(parsed).length > 0 ? yaml.dump(parsed, { indent: 2, lineWidth: -1, noRefs: true }) : "";
  } catch (e) {
    return values;
  }
};

// parseValuesText returns the parsed values object of the YAML text (JSON is valid YAML), an empty text gives an empty object.
// It returns undefined when the text is not a YAML mapping.
export const parseValuesText = (text: string): { [key: string]: any } | undefined => {
  if (!text.trim()) return {};
  try {
    const parsed = yaml.load(text, { schema: yaml.CORE_SCHEMA });
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as { [key: string]: any }) : undefined;
  } catch (e) {
    return undefined;
  }
};

// encodeValues encodes the YAML text as JSON for the API, it returns undefined for no values.
export const encodeValues = (text: string): string | undefined => {
  const parsed = parseValuesText(text);
  if (!parsed || Object.keys(parsed).length === 0) return undefined;
  return toBase64(JSON.stringify(parsed));
};

export const decodeValuesObject = (values?: string): { [key: string]: any } => {
  try {
    return (values && parseValuesText(fromBase64(values))) || {};
  } catch (e) {
    return {};
  }
};

// flattenValues flattens the values to Helm style paths. Dots and backslashes in a key are escaped
// with a backslash, like chartd does for the value paths it reports (prometheus\.io/scrape).
export const flattenValues = (value: any, prefix: string = "", out: { [path: string]: any } = {}) => {
  const isObject = value && typeof value === "object" && !Array.isArray(value);
  if (!isObject) {
    if (prefix) out[prefix] = value;
    return out;
  }
  const keys = Object.keys(value);
  if (keys.length === 0 && prefix) {
    out[prefix] = {};
    return out;
  }
  keys.forEach((key) => {
    const escaped = key.replace(/\\/g, "\\\\").replace(/\./g, "\\.");
    flattenValues(value[key], prefix ? `${prefix}.${escaped}` : escaped, out);
  });
  return out;
};

// sourceName returns template, configuration or deployment. The API may send the enum as a name or a number.
export const sourceName = (source?: ChartValueSource | string): string => {
  const name = typeof source === "number" ? ChartValueSource[source] || "" : source || "";
  const short = name.replace("CHART_VALUE_SOURCE_", "").toLowerCase();
  return short === "unspecified" ? "" : short;
};

const sourceColors: { [source: string]: SemanticCOLORS } = {
  template: "grey",
  configuration: "blue",
  deployment: "purple",
};

const sourceHints: { [source: string]: string } = {
  template: "From the charts.yaml template of this release.",
  configuration: "Set in the chart configuration of this environment (Configuration tab).",
  deployment: "Edited on the deployment itself.",
};

export const redundantHint = "The configured value equals the template, the configuration entry can be cleared.";

export const SourceLabel = ({ source }: { source?: ChartValueSource | string }) => {
  const name = sourceName(source);
  if (!name) return null;
  return (
    <Label size="tiny" basic color={sourceColors[name]} title={sourceHints[name]}>
      {name}
    </Label>
  );
};

// SourceLegend explains the labels that show where a chart field or value comes from.
export const SourceLegend = ({ sources }: { sources: string[] }) => (
  <div style={{ marginBottom: "1rem" }}>
    {sources.map((source) => (
      <span key={source} style={{ marginRight: "1.5rem" }}>
        <SourceLabel source={source} /> {sourceHints[source]}
      </span>
    ))}
    <span>
      <Label size="tiny" basic color="orange">
        same as template
      </Label>{" "}
      {redundantHint}
    </span>
  </div>
);

export const errorText = (error: any): string | undefined => {
  if (!error) return undefined;
  return error.message || (typeof error === "string" ? error : JSON.stringify(error));
};

// ConfirmWrite asks for confirmation before an action that changes the chart configuration.
// Without always the confirmation is only asked in production.
export type ConfirmWrite = (
  options: { header: string; content?: string; warning?: string; confirm?: string; always?: boolean },
  action: () => Promise<void>
) => void;

// downloadText downloads the text as a file.
export const downloadText = (filename: string, text: string) => {
  const url = URL.createObjectURL(new Blob([text], { type: "text/yaml" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

// isError returns true for a diagnostic that blocks storing. The API may send the enum as a name or a number.
export const isError = (diagnostic: ChartDiagnostic): boolean =>
  diagnostic.severity === ChartDiagnosticSeverity.CHART_DIAGNOSTIC_SEVERITY_ERROR || diagnostic.severity === ("CHART_DIAGNOSTIC_SEVERITY_ERROR" as any);

export const hasErrors = (diagnostics?: ChartDiagnostic[]): boolean => !!diagnostics && diagnostics.some(isError);

// DiagnosticsList shows the problems that chartd found in a configuration or in deployment overrides.
export const DiagnosticsList = ({ diagnostics }: { diagnostics?: ChartDiagnostic[] }) => {
  if (!diagnostics || diagnostics.length === 0) return null;
  return (
    <>
      {diagnostics.map((d, i) => (
        <Message key={i} size="small" negative={isError(d)} warning={!isError(d)} style={{ marginTop: "0.5rem" }}>
          <code>{[d.chart, d.path].filter((part) => !!part).join(" ")}</code> {d.message}
        </Message>
      ))}
    </>
  );
};
